import React, { useEffect, useState, useCallback } from "react";
import { Save, Globe, Share2, Twitter, ShieldCheck, MapPin, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useSEO } from "@/context/SEOContext";

const EMPTY = {
  meta: { title: "", description: "", keywords: "", canonical_base: "", robots_index: true, robots_follow: true },
  og: { title: "", description: "", image: "", type: "website", site_name: "" },
  twitter: { title: "", description: "", image: "", card_type: "summary_large_image" },
  verification: { google: "", bing: "", facebook: "" },
  local_business: { enabled: true, opening_hours: "Mo-Su 08:00-20:00", price_range: "$$" },
};

export default function AdminSEOSettings() {
  const { reload } = useSEO();
  const [s, setS] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/seo");
      if (r.data) setS({ ...EMPTY, ...r.data, meta: { ...EMPTY.meta, ...(r.data.meta || {}) }, og: { ...EMPTY.og, ...(r.data.og || {}) }, twitter: { ...EMPTY.twitter, ...(r.data.twitter || {}) }, verification: { ...EMPTY.verification, ...(r.data.verification || {}) }, local_business: { ...EMPTY.local_business, ...(r.data.local_business || {}) } });
    } catch (e) { notifyError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (section, field, value) =>
    setS((x) => ({ ...x, [section]: { ...(x[section] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/seo", s);
      toast.success("SEO tersimpan. Halaman akan otomatis update.");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="p-8 text-center text-[#5C5C5C]">Memuat SEO...</div></AdminLayout>;

  return (
    <AdminLayout title="Platform SEO">
      <div className="max-w-4xl space-y-6" data-testid="admin-seo-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-black">Platform SEO</h1>
            <p className="text-sm text-[#5C5C5C] mt-1">Kelola meta tag, Open Graph, Twitter Card, verifikasi search engine dan schema. Kosongkan field untuk fallback otomatis ke Branding.</p>
          </div>
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl font-bold h-11 px-5" data-testid="seo-save-btn">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan SEO"}
          </Button>
        </div>

        {/* Quick links to sitemap & robots */}
        <Card className="p-4 bg-white flex items-center gap-3 flex-wrap" data-testid="seo-tools-card">
          <span className="text-xs text-[#5C5C5C]">Tools:</span>
          <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs brand-text-primary font-bold inline-flex items-center gap-1 hover:underline" data-testid="seo-sitemap-link"><ExternalLink size={12} /> sitemap.xml</a>
          <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="text-xs brand-text-primary font-bold inline-flex items-center gap-1 hover:underline" data-testid="seo-robots-link"><ExternalLink size={12} /> robots.txt</a>
          <span className="text-[10px] text-[#5C5C5C] ml-auto">Note: di production, nginx rewrite <code>/sitemap.xml</code> → <code>/api/sitemap.xml</code></span>
        </Card>

        {/* General Meta */}
        <Card className="p-6" data-testid="seo-meta-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Globe size={18} className="brand-text-primary" /> General Meta</h3>
          <div className="grid gap-3">
            <div><Label>Meta Title <span className="text-[10px] text-[#5C5C5C]">(kosongkan untuk pakai Website Name)</span></Label><Input value={s.meta.title} onChange={(e) => upd("meta", "title", e.target.value)} placeholder="Nama Toko — Tagline Toko" data-testid="seo-meta-title" /></div>
            <div><Label>Meta Description</Label><Textarea value={s.meta.description} onChange={(e) => upd("meta", "description", e.target.value)} rows={2} placeholder="Deskripsi 155-160 karakter untuk hasil Google." data-testid="seo-meta-description" /></div>
            <div><Label>Meta Keywords <span className="text-[10px] text-[#5C5C5C]">(pisahkan dengan koma)</span></Label><Input value={s.meta.keywords} onChange={(e) => upd("meta", "keywords", e.target.value)} placeholder="telur, ayam kampung, telur puyuh" data-testid="seo-meta-keywords" /></div>
            <div><Label>Canonical Base URL <span className="text-[10px] text-[#5C5C5C]">(mis. https://tokoanda.com — tanpa slash akhir)</span></Label><Input value={s.meta.canonical_base} onChange={(e) => upd("meta", "canonical_base", e.target.value)} placeholder="https://tokoanda.com" data-testid="seo-canonical-base" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-center justify-between p-3 rounded-xl border border-[#E5E5E5]">
                <div><div className="font-bold text-sm">Robots: Index</div><div className="text-[11px] text-[#5C5C5C]">Izinkan mesin pencari meng-index</div></div>
                <Switch checked={s.meta.robots_index} onCheckedChange={(v) => upd("meta", "robots_index", v)} data-testid="seo-robots-index" />
              </label>
              <label className="flex items-center justify-between p-3 rounded-xl border border-[#E5E5E5]">
                <div><div className="font-bold text-sm">Robots: Follow</div><div className="text-[11px] text-[#5C5C5C]">Izinkan follow link</div></div>
                <Switch checked={s.meta.robots_follow} onCheckedChange={(v) => upd("meta", "robots_follow", v)} data-testid="seo-robots-follow" />
              </label>
            </div>
          </div>
        </Card>

        {/* Open Graph */}
        <Card className="p-6" data-testid="seo-og-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Share2 size={18} className="brand-text-primary" /> Open Graph (Facebook, LinkedIn, WhatsApp)</h3>
          <div className="grid gap-3">
            <div><Label>OG Title <span className="text-[10px] text-[#5C5C5C]">(fallback: Meta Title)</span></Label><Input value={s.og.title} onChange={(e) => upd("og", "title", e.target.value)} data-testid="seo-og-title" /></div>
            <div><Label>OG Description</Label><Textarea value={s.og.description} onChange={(e) => upd("og", "description", e.target.value)} rows={2} data-testid="seo-og-description" /></div>
            <div><Label>OG Image URL <span className="text-[10px] text-[#5C5C5C]">(1200×630 recommended)</span></Label><Input value={s.og.image} onChange={(e) => upd("og", "image", e.target.value)} placeholder="/brand/og-cover.png" className="font-mono text-xs" data-testid="seo-og-image" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>OG Type</Label><Input value={s.og.type} onChange={(e) => upd("og", "type", e.target.value)} placeholder="website" data-testid="seo-og-type" /></div>
              <div><Label>OG Site Name</Label><Input value={s.og.site_name} onChange={(e) => upd("og", "site_name", e.target.value)} placeholder="Nama Toko Anda" data-testid="seo-og-sitename" /></div>
            </div>
          </div>
        </Card>

        {/* Twitter Card */}
        <Card className="p-6" data-testid="seo-twitter-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Twitter size={18} className="brand-text-primary" /> Twitter Card</h3>
          <div className="grid gap-3">
            <div><Label>Twitter Title</Label><Input value={s.twitter.title} onChange={(e) => upd("twitter", "title", e.target.value)} data-testid="seo-tw-title" /></div>
            <div><Label>Twitter Description</Label><Textarea value={s.twitter.description} onChange={(e) => upd("twitter", "description", e.target.value)} rows={2} data-testid="seo-tw-description" /></div>
            <div><Label>Twitter Image URL</Label><Input value={s.twitter.image} onChange={(e) => upd("twitter", "image", e.target.value)} className="font-mono text-xs" data-testid="seo-tw-image" /></div>
            <div><Label>Card Type</Label>
              <select value={s.twitter.card_type} onChange={(e) => upd("twitter", "card_type", e.target.value)} className="w-full mt-1 border border-[#E5E5E5] rounded-lg p-2 text-sm bg-white" data-testid="seo-tw-card">
                <option value="summary">summary</option>
                <option value="summary_large_image">summary_large_image</option>
                <option value="app">app</option>
                <option value="player">player</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Verification */}
        <Card className="p-6" data-testid="seo-verification-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><ShieldCheck size={18} className="brand-text-primary" /> Verifikasi Search Engine</h3>
          <div className="grid gap-3">
            <div><Label>Google Search Console Verification <span className="text-[10px] text-[#5C5C5C]">(hanya content dari meta tag)</span></Label><Input value={s.verification.google} onChange={(e) => upd("verification", "google", e.target.value)} placeholder="abc123..." data-testid="seo-verify-google" /></div>
            <div><Label>Bing Verification</Label><Input value={s.verification.bing} onChange={(e) => upd("verification", "bing", e.target.value)} data-testid="seo-verify-bing" /></div>
            <div><Label>Facebook Domain Verification</Label><Input value={s.verification.facebook} onChange={(e) => upd("verification", "facebook", e.target.value)} data-testid="seo-verify-facebook" /></div>
          </div>
        </Card>

        {/* Local Business */}
        <Card className="p-6" data-testid="seo-local-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><MapPin size={18} className="brand-text-primary" /> Local Business Schema</h3>
          <p className="text-xs text-[#5C5C5C] mb-3">Untuk UMKM & toko lokal — meningkatkan peluang tampil di Google Maps / Rich Results. Alamat, WhatsApp, jam operasional diambil dari Branding & field di bawah.</p>
          <label className="flex items-center justify-between p-3 rounded-xl border border-[#E5E5E5] mb-3">
            <div><div className="font-bold text-sm">Aktifkan Schema LocalBusiness</div><div className="text-[11px] text-[#5C5C5C]">Inject JSON-LD di semua halaman utama</div></div>
            <Switch checked={s.local_business.enabled} onCheckedChange={(v) => upd("local_business", "enabled", v)} data-testid="seo-local-enabled" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Jam Operasional <span className="text-[10px] text-[#5C5C5C]">(format schema.org)</span></Label><Input value={s.local_business.opening_hours} onChange={(e) => upd("local_business", "opening_hours", e.target.value)} placeholder="Mo-Su 08:00-20:00" data-testid="seo-local-hours" /></div>
            <div><Label>Price Range</Label><Input value={s.local_business.price_range} onChange={(e) => upd("local_business", "price_range", e.target.value)} placeholder="$$" data-testid="seo-local-price" /></div>
          </div>
        </Card>

        <div className="sticky bottom-4 flex gap-2">
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl h-12 px-6 font-bold shadow-lg" data-testid="seo-save-btn-bottom">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan SEO"}
          </Button>
          <Button onClick={load} variant="outline" className="rounded-xl h-12" data-testid="seo-reload-btn">
            <RefreshCw size={16} className="mr-1" /> Reload
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
